import { useWishlistEntryPoints } from 'src/lib/optimizely/experiments/use-wishlist-entry-points-visible'
import { Footer } from '@awaze/design-system.compositions.footer'
import {
  HeaderExtended,
  ResponsiveHeader,
} from '@awaze/design-system.compositions.responsive-header'
import { renderContentfulRichText } from '@awaze/mfe-common.lib.contentful/dist/api/utils/render-richtext'
import React, { useContext } from 'react'
import { useLocations } from 'src/components/LocationSelector/useLocations'
import { richTextOptions } from 'src/lib/contentful/contentful.utils'
import { GlobalProps } from 'src/utils/globalProps'
import { Container } from '@awazevr/design-system.components.container'
import { SearchFormConfig } from '@awaze/design-system.compositions.search-form'
import { IHeaderOptionsModuleFields } from 'contentful/types/contentful-modules'
import { HeroWithImageCompression } from '@/components/Hero/Hero'
import { SearchFormComponent } from 'src/components/SearchForm/SearchForm'
import { AWSAccountBanner } from 'src/components/Utils/AWSAccountBanner'
import { PreviewBanner } from 'src/components/Utils/PreviewBanner'
import { BreadcrumbItems } from 'src/utils/breadcrumb/breadcrumb-utils'
import { GlobalStyling } from 'src/utils/globalstyle'
import {
  IFooterFields,
  IHeaderFields,
} from '../../@types/generated/contentful-global'
import Breadcrumb from '@/components/Breadcrumbs/Breadcrumb'
import useDatalayerOnClick from 'src/utils/dataLayer/DatalayerOnClick/useDatalayerOnClick'
import useOptimizelyExperimentsDatalayer from 'src/utils/dataLayer/useOptimizelyExperimentsDatalayer'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { unwrapMenu } from './GenericPageLayoutMap'
import { useGuestAuth } from '@awaze/mfe-common.contexts.guest-auth'
import ErrorBoundaryWrapper from '@/components/Utils/ErrorBoundery'
import {
  deepConvertLinks,
  convertLinkToAbsoluteUrl,
} from '../utils/helpers/helpers'

export type GenericPageLayoutProps = {
  children: React.ReactNode
  isPreview?: boolean
  contentfulId?: string
  header: IHeaderFields
  footer: IFooterFields
  searchFormConfig: SearchFormConfig
  headerOptions?: IHeaderOptionsModuleFields
  newAWSAccount?: boolean
  breadcrumbs: BreadcrumbItems[]
  showPhoneIcon: boolean
  brand: Brand
}

export const GenericPageLayout: React.FC<GenericPageLayoutProps> = ({
  isPreview,
  contentfulId,
  brand,
  header,
  footer,
  searchFormConfig,
  headerOptions,
  newAWSAccount,
  breadcrumbs,
  showPhoneIcon,
  children,
}) => {
  const { contentfulLocale, host } = useContext(GlobalProps)
  const locations = useLocations()
  const { session } = useGuestAuth()

  //Datalayer for Optimizely experiments
  useOptimizelyExperimentsDatalayer()

  const { ref } = useDatalayerOnClick()

  // Optimizely experiment for wishlist icon
  const wishListIcon = unwrapMenu(header.desktopMenu?.fields?.menuItems).find(
    (item) => item.type === 'wishlist',
  )
  const showWishlistEntryPoints = useWishlistEntryPoints()
  const shouldShowWishlistIcon =
    !!wishListIcon && !!wishListIcon.link?.href && showWishlistEntryPoints

  const homePageLink = convertLinkToAbsoluteUrl('/', host)

  // This function convert all links in Mega menu if they are relative format
  const desktopMenuItems = deepConvertLinks(
    header.desktopMenu?.fields?.menuItems,
    host,
  )
  const mobileMenuItems = deepConvertLinks(
    header.mobileMenu?.fields?.menuItems,
    host,
  )
  const footerMenuItems = deepConvertLinks(footer.menu?.fields?.menuItems, host)

  // @TODO refactor styles for jamesVillas shadow hack
  return (
    <>
      <div ref={ref}>
        {isPreview && <PreviewBanner contentfulId={contentfulId} />}
        {newAWSAccount && <AWSAccountBanner />}
        <div
          id={
            brand === 'jamesVillas'
              ? 'james-villas-shadow-hack-header-GD-369'
              : undefined
          }
        >
          <ErrorBoundaryWrapper>
            <ResponsiveHeader
              user={session?.user}
              wishlist={{
                displayWishlistIcon: shouldShowWishlistIcon,
                label: wishListIcon?.link?.displayText || '',
                href: wishListIcon?.link?.href
                  ? convertLinkToAbsoluteUrl(wishListIcon?.link?.href)
                  : '',
              }}
              logoLink={homePageLink}
              phoneNumber={{
                displayPhoneIcon: showPhoneIcon,
                href: header.desktopMenu?.fields?.menuItems?.[0]?.fields?.link
                  ?.fields?.href,
                label:
                  header.desktopMenu?.fields?.menuItems?.[0]?.fields?.title,
              }}
              brand={brand}
              locale={contentfulLocale}
              menuItems={{
                mobileMenu: unwrapMenu(mobileMenuItems) ?? [],
                desktopMenu: unwrapMenu(desktopMenuItems) ?? [],
              }}
              labels={{
                menuButtonText: header.menuButtonText,
                closeButtonText: header.closeButtonText,
              }}
              showMegaMenu
            />
          </ErrorBoundaryWrapper>
        </div>
        {headerOptions && (
          <HeroWithImageCompression
            searchFormConfig={searchFormConfig}
            headerOptions={headerOptions}
            alertBanner={header.alertBanner}
            experimentEnabled={false}
          />
        )}
        {!headerOptions && (
          <div
            id={
              brand === 'jamesVillas'
                ? 'james-villas-shadow-hack-wrapper-GD-369'
                : undefined
            }
          >
            <HeaderExtended isFlush={true}>
              <Container>
                <SearchFormComponent
                  config={searchFormConfig}
                  isSummary={true}
                />
              </Container>
            </HeaderExtended>
          </div>
        )}
        {breadcrumbs && (
          <div
            id={
              brand === 'jamesVillas'
                ? 'james-villas-breadcrumbs-hack-wrapper-GD-369'
                : undefined
            }
          >
            <Breadcrumb breadcrumbs={breadcrumbs} />
          </div>
        )}
        <div className="content-container" id="content-container">
          {children}
        </div>
        <ErrorBoundaryWrapper>
          <Footer
            locationSelect={locations}
            labels={{
              menu: {
                menuItems: unwrapMenu(footerMenuItems) ?? [],
              },
              panels:
                footer?.panels?.map((p) => ({
                  cta: {
                    text: p.fields?.cta?.fields?.text,
                    title: p.fields?.cta?.fields?.title,
                    icon: p.fields?.cta?.fields?.icon,
                    link: convertLinkToAbsoluteUrl(
                      p.fields?.cta?.fields?.link,
                      host,
                    ),
                  },
                  text: p.fields?.text,
                  title: p.fields?.title,
                  icon: p.fields?.icon,
                  secondaryText: p.fields?.secondaryText,
                })) ?? [],
              socialMediaIcons:
                footer?.socialMediaIcons?.map((i) => ({
                  displayText: i.fields?.displayText,
                  href: i.fields?.href,
                  openInNew: i.fields?.openInNew,
                  rel: i.fields?.rel,
                })) ?? [],
              termsAndConditions: {
                displayText: footer?.termsAndConditions?.fields?.displayText,
                href: convertLinkToAbsoluteUrl(
                  footer?.termsAndConditions?.fields?.href,
                  host,
                ),
                openInNew: footer?.termsAndConditions?.fields?.openInNew,
                rel: footer?.termsAndConditions?.fields?.rel,
              },
              companyInformation: renderContentfulRichText(
                footer?.companyInformation,
                richTextOptions,
              ) as React.ReactElement,
            }}
            styleProps={{
              accordion: {
                theme: {
                  accordion: 'dark',
                },
              },
            }}
          />
        </ErrorBoundaryWrapper>
      </div>
      <GlobalStyling />
    </>
  )
}
